import type { Block, BlockOperation, Workflow } from './workflow';

/**
 * The agent seam.
 *
 * One function stands between the canvas and whatever produces block
 * operations. Today that is a local rule-based stand-in; when the real agent
 * lands it becomes an HTTP call and nothing else in the app changes — the
 * canvas already applies operations through `applyOperations`, which is the
 * same path its own controls use.
 *
 * Set `VITE_AGENT_ENDPOINT` to switch. See `docs/HANDOVER_AGENT.md` for the
 * wire contract the endpoint must honour.
 *
 * The stand-in is deliberately *not* a canned script. It emits real operations
 * carrying real endpoints, so blocks it adds fetch live data and render
 * identically to template blocks. That keeps the UI honest: if the layout,
 * empty states or scroll behaviour are wrong, they are wrong here too.
 */

export interface AgentTurnRequest {
  message: string;
  /** Full document, so the agent can reference existing blocks by id. */
  workflow: Workflow;
}

export interface AgentTurn {
  /** Prose for the transcript. Never rendered onto the canvas. */
  message: string;
  operations: BlockOperation[];
}

const AGENT_ENDPOINT = import.meta.env.VITE_AGENT_ENDPOINT ?? '';

export function isAgentLive(): boolean {
  return Boolean(AGENT_ENDPOINT);
}

export async function sendAgentTurn(
  request: AgentTurnRequest,
  signal?: AbortSignal
): Promise<AgentTurn> {
  if (!AGENT_ENDPOINT) return localTurn(request, signal);

  const res = await fetch(AGENT_ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      message: request.message,
      workflow: {
        scope: request.workflow.scope,
        inputs: request.workflow.inputs,
        blocks: request.workflow.blocks,
      },
    }),
    signal,
  });

  if (!res.ok) throw new Error(`Agent request failed: ${res.status}`);

  const data = (await res.json()) as Partial<AgentTurn>;
  return {
    message: data.message ?? '',
    /* An agent that returns a malformed operation list should degrade to a
       chat reply, never crash the canvas. */
    operations: Array.isArray(data.operations) ? data.operations : [],
  };
}

// ─── Local stand-in ────────────────────────────────────────────────────────

/**
 * What the workflow is about, which decides the endpoints a new block gets.
 * A block added to a Title Valuation must read the title endpoints; the same
 * request on a Platform Scorecard must read platform-shaped ones.
 */
type Subject =
  | { kind: 'title' }
  | { kind: 'platform' }
  | { kind: 'catalogue'; searchable: boolean };

function subjectOf(workflow: Workflow): Subject {
  if ('titleId' in workflow.inputs) return { kind: 'title' };
  if ('platform' in workflow.inputs) return { kind: 'platform' };
  return { kind: 'catalogue', searchable: 'search' in workflow.inputs };
}

/** Scope params every leaderboard-backed block carries. */
function leaderboardParams(
  subject: Extract<Subject, { kind: 'catalogue' }>,
  extra: Record<string, string> = {}
): Record<string, string> {
  return {
    ...(subject.searchable ? { search: '{{search}}' } : {}),
    region: '{{region}}',
    timePeriod: '{{timePeriod}}',
    ...extra,
  };
}

type Draft = Omit<Block, 'id'>;

/** One thing the stand-in knows how to build. */
interface Intent {
  /** Matched against the lowercased message. */
  test: RegExp;
  /** Null when the intent cannot be expressed for this subject. */
  build: (subject: Subject) => Draft | null;
  /** Narration for the transcript. */
  say: string;
}

const INTENTS: Intent[] = [
  {
    test: /\b(region|regional|geograph|market|territor|where)\b/,
    say: 'a regional breakdown',
    build: (subject) => {
      if (subject.kind === 'title') {
        return {
          type: 'donut',
          title: 'Revenue by region',
          subtitle: 'Where the value is earned',
          source: { endpoint: '/streaming-economics/titles/{{titleId}}/revenue' },
          options: { groupBy: 'region' },
        };
      }
      if (subject.kind === 'platform') {
        return {
          type: 'donut',
          title: 'Subscribers by region',
          subtitle: 'Where the base sits',
          source: { endpoint: '/streaming-economics/platforms/{{platform}}/history' },
          options: { shape: 'platform', groupBy: 'region', metric: 'subscribers' },
        };
      }
      return {
        type: 'donut',
        title: 'Value by region',
        subtitle: 'How far the catalogue travels',
        source: { endpoint: '/streaming-economics/leaderboard', params: leaderboardParams(subject, { limit: '200' }) },
        options: { groupBy: 'region' },
      };
    },
  },
  {
    test: /\b(platform|streamer|carrier|netflix|disney|max|peacock|hulu)\b/,
    say: 'a platform breakdown',
    build: (subject) => {
      if (subject.kind === 'title') {
        return {
          type: 'donut',
          title: 'Revenue by platform',
          subtitle: 'Across every platform carrying the title',
          source: { endpoint: '/streaming-economics/titles/{{titleId}}/revenue' },
          options: { groupBy: 'platform' },
        };
      }
      if (subject.kind === 'platform') return null;
      return {
        type: 'donut',
        title: 'Value by platform',
        subtitle: 'Where the catalogue earns',
        source: { endpoint: '/streaming-economics/leaderboard', params: leaderboardParams(subject, { limit: '200' }) },
        options: { groupBy: 'platform' },
      };
    },
  },
  {
    test: /\b(genre|category|comedy|drama)\b/,
    say: 'a genre split',
    build: (subject) =>
      subject.kind === 'catalogue'
        ? {
            type: 'donut',
            title: 'Value by genre',
            subtitle: 'Which genres carry the catalogue',
            source: {
              endpoint: '/streaming-economics/leaderboard',
              params: leaderboardParams(subject, { limit: '200' }),
            },
            options: { groupBy: 'genre' },
          }
        : null,
  },
  {
    test: /\b(trend|over time|time series|timeseries|quarter|growth|trajector|histor)\b/,
    say: 'the trend over time',
    build: (subject) => {
      if (subject.kind === 'title') {
        return {
          type: 'timeseries',
          title: 'Revenue over time',
          subtitle: 'Acquisition and retention revenue by quarter',
          source: { endpoint: '/streaming-economics/titles/{{titleId}}/revenue/time-series' },
        };
      }
      if (subject.kind === 'platform') {
        return {
          type: 'timeseries',
          title: 'Subscribers over time',
          subtitle: 'Every quarter on record',
          source: { endpoint: '/streaming-economics/platforms/{{platform}}/history' },
          options: { shape: 'platform', metric: 'subscribers' },
        };
      }
      return null;
    },
  },
  {
    test: /\b(table|list|rank|ranking|top|leaderboard|titles|best|biggest)\b/,
    say: 'a ranked table',
    build: (subject) =>
      subject.kind === 'catalogue'
        ? {
            type: 'table',
            title: 'Ranked titles',
            subtitle: 'By revenue contribution',
            source: {
              endpoint: '/streaming-economics/leaderboard',
              params: leaderboardParams(subject, { limit: '20' }),
            },
            options: { limit: 20 },
          }
        : {
            type: 'table',
            title: 'Platform leaderboard',
            subtitle: 'Ranked by subscribers',
            source: { endpoint: '/streaming-economics/platforms/leaderboard', params: { region: '{{region}}' } },
            options: { shape: 'platform', limit: 12 },
          },
  },
  {
    test: /\b(acquisition|retention|acquire|retain|churn|win|keep)\b/,
    say: 'the acquisition-versus-retention split',
    build: (subject) =>
      subject.kind === 'title'
        ? {
            type: 'split',
            title: 'Acquisition vs retention',
            subtitle: 'Did this title win subscribers, or keep them?',
            source: { endpoint: '/streaming-economics/titles/{{titleId}}/revenue' },
          }
        : subject.kind === 'catalogue'
          ? {
              type: 'split',
              title: 'Acquisition vs retention',
              subtitle: 'What this catalogue does',
              source: {
                endpoint: '/streaming-economics/leaderboard',
                params: leaderboardParams(subject, { limit: '200' }),
              },
            }
          : null,
  },
  {
    test: /\b(kpi|headline|summary|glance|overview|figures|numbers|total)\b/,
    say: 'the headline figures',
    build: (subject) => {
      if (subject.kind === 'title') {
        return {
          type: 'kpis',
          title: 'Headline figures',
          source: { endpoint: '/streaming-economics/titles/{{titleId}}/revenue' },
        };
      }
      if (subject.kind === 'platform') {
        return {
          type: 'kpis',
          title: 'This quarter',
          source: { endpoint: '/streaming-economics/platforms/leaderboard', params: { region: '{{region}}' } },
          options: { shape: 'platform', platform: '{{platform}}' },
        };
      }
      return {
        type: 'kpis',
        title: 'At a glance',
        source: {
          endpoint: '/streaming-economics/leaderboard',
          params: leaderboardParams(subject, { limit: '200' }),
        },
      };
    },
  },
];

/** Blocks whose title best matches the words in a removal request. */
function matchBlocks(workflow: Workflow, message: string): Block[] {
  const words = message
    .toLowerCase()
    .replace(/[^a-z\s]/g, ' ')
    .split(/\s+/)
    .filter((w) => w.length > 3);
  return workflow.blocks.filter((block) => {
    const title = block.title.toLowerCase();
    return words.some((word) => title.includes(word)) || title.includes(message.toLowerCase());
  });
}

const THINKING_MS = 550;

function wait(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(resolve, ms);
    signal?.addEventListener('abort', () => {
      clearTimeout(timer);
      reject(new DOMException('Aborted', 'AbortError'));
    });
  });
}

async function localTurn(
  { message, workflow }: AgentTurnRequest,
  signal?: AbortSignal
): Promise<AgentTurn> {
  /* A beat before answering. Not cosmetic — it exercises the pending state,
     the abort button and the transcript's scroll-on-append, which are the
     things that break once a real endpoint introduces latency. */
  await wait(THINKING_MS, signal);

  const text = message.toLowerCase();
  const subject = subjectOf(workflow);

  if (/\b(clear|reset|empty|start over|remove everything|delete all)\b/.test(text)) {
    if (workflow.blocks.length === 0) {
      return { message: 'The canvas is already empty.', operations: [] };
    }
    return {
      message: `Cleared all ${workflow.blocks.length} blocks. Ask for anything to start again.`,
      operations: workflow.blocks.map((b) => ({ op: 'remove', blockId: b.id })),
    };
  }

  /* Re-shaping an existing block, e.g. "change revenue by region to a bar".
     Runs before the remove and add branches: the phrase names a block *and*
     usually a dimension, so an add-intent would otherwise match first and
     report the block as already present. */
  const askedChart = (['bar', 'line', 'area', 'donut'] as const).find((t) =>
    new RegExp(`\\b${t}\\b`).test(text)
  );

  if (askedChart && /\b(change|switch|make|turn|convert|swap)\b/.test(text)) {
    const targets = matchBlocks(workflow, text).filter((b) =>
      ['chart', 'donut', 'timeseries'].includes(b.type)
    );
    if (targets.length === 0) {
      return {
        message: "I couldn't tell which chart you meant. Name it as it appears on the canvas.",
        operations: [],
      };
    }
    const target = targets[0];
    return {
      message: `Redrew “${target.title}” as ${askedChart === 'area' ? 'an' : 'a'} ${askedChart} chart.`,
      operations: [
        {
          op: 'replace',
          blockId: target.id,
          block: {
            ...target,
            type: 'chart',
            options: { ...target.options, chartType: askedChart },
          },
        },
      ],
    };
  }

  if (/\b(remove|delete|drop|get rid of|take out)\b/.test(text)) {
    const targets = matchBlocks(workflow, text);
    if (targets.length === 0) {
      return {
        message: "I couldn't tell which block you meant. Name it as it appears on the canvas.",
        operations: [],
      };
    }
    return {
      message: `Removed ${targets.map((b) => `“${b.title}”`).join(' and ')}.`,
      operations: targets.map((b) => ({ op: 'remove', blockId: b.id })),
    };
  }

  const matched = INTENTS.filter((intent) => intent.test.test(text));
  const drafts = matched
    .map((intent) => ({ intent, draft: intent.build(subject) }))
    .filter((entry): entry is { intent: Intent; draft: Draft } => entry.draft !== null);

  if (drafts.length === 0) {
    return {
      message:
        matched.length > 0
          ? "That doesn't apply to this workflow's subject — try a regional or platform breakdown, a trend, or a ranked table."
          : 'I can add headline figures, a trend over time, a ranked table, or a breakdown by region, platform or genre. Ask for one, or say "remove" and a block name.',
      operations: [],
    };
  }

  /* Adding something already on the canvas is almost never what was meant. */
  const existing = new Set(workflow.blocks.map((b) => b.title));
  const fresh = drafts.filter((entry) => !existing.has(entry.draft.title));

  if (fresh.length === 0) {
    return {
      message: `${drafts.map((d) => `“${d.draft.title}”`).join(' and ')} ${
        drafts.length === 1 ? 'is' : 'are'
      } already on the canvas.`,
      operations: [],
    };
  }

  return {
    message: `Added ${fresh.map((entry) => entry.intent.say).join(' and ')}.`,
    operations: fresh.map((entry) => ({ op: 'add', block: entry.draft })),
  };
}
